import { FC } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Message } from 'tedooo-web-design-system/ui';
import { HelpCircle } from 'tedooo-web-design-system/icons';

import {
  DiscoverListsId,
  IStore2,
  LikeProductSource,
  popupIdsOptions,
  ProductPageSources,
} from 'types';
import Information from '../../information/Information';
import './home.scss';
import ActivitiesSection from './activitiesSection/ActivitiesSection';
import ReviewsSection from 'pages/shopPage/content/home/reviewsSection/ReviewsSection';
import CatalogSection from './catalogSection/CatalogSection';
import { ROUTES } from 'constant';
import SimilarShopsSection from './similarShopsSection/SimilarShopsSection';
import { IShopPageHome } from './types';
import { buildDiscoverPageQuery, checkSmallScreen, toDateFormatWithText } from 'utils';
import { useRequireToken } from 'hooks/useRequireToken';
import { PopupSlicer } from 'store/popup/PopupActions';
import GroupsSection from 'pages/profilePage/groupsSection/GroupsSection';
import AnalyticsSection from 'pages/profilePage/analytics/AnalyticsSection';
import { CreateShopSlicer } from 'store/createShop/CreateShopSlicer';
import { onFollowUserToggle } from 'store/profile/ProfileActions';
import Tip from 'components/tip/Tip';
import { onProductLikeToggle } from 'store/specificProduct/SpecificProductActions';
import { translations } from 'translations/en';
import { SpecificProductSlicer } from 'store/specificProduct/SpecificProductSlicer';

const isMobile = checkSmallScreen();

const Home: FC<IShopPageHome> = ({
  shop,
  tip,
  vacationDetails,
  isShopOwner,
  onActivitiesClick,
  onSeeAllCatalogClick,
  onReviewsClick,
  onFollowToggle,
  showPopup,
  onAddItemToCatalogClick,
  editListing,
  duplicateListing,
  updateProductPageSource,
  onLikeToggle,
}) => {
  const navigate = useNavigate();

  const { details } = shop;

  if (!details) {
    return null;
  }

  const onActivityClick = (id: string) => navigate(`/${ROUTES.POST}/${id}`);

  const onShopClick = useRequireToken((id: string) => {
    navigate(`/${ROUTES.SHOP}/${id}`);
  });

  const {
    totalProfileViews,
    totalSavedProducts,
    groups,
    hasNewItems,
    categories,
    activities,
    catalog,
    description,
    reviews,
    id,
    similarShops,
  } = details;

  const onSeeAllSimilarShops = useRequireToken(() => navigate(`/${ROUTES.SIMILAR_SHOPS}/${id}`));

  const onPreviewClick = (index: number) => {
    updateProductPageSource({ source: ProductPageSources.Shop });
    navigate(`/${ROUTES.PRODUCT}/${catalog[index].id}`);
  };

  const onAllChatsClick = () => navigate(`/${ROUTES.CHAT}`);

  const onViewsClick = () => showPopup({ id: popupIdsOptions.profileViewsPopup });

  const onSavesClick = () => {
    const query = buildDiscoverPageQuery({
      id: DiscoverListsId.Favorites,
      title: translations.PRODUCTS_PAGE.FAVORITES_TITLE,
      paginationToken: null,
    });
    navigate(`/${ROUTES.PRODUCTS}${query}`);
  };

  const onActivityEmptyStateClick = () => {
    showPopup({ id: popupIdsOptions.createPostPopup });
  };

  const onDeleteClick = (productId: string) => {
    showPopup({ id: popupIdsOptions.deleteItemPopup, productId });
  };

  const onEditClick = (productId: string) => {
    editListing({ id: productId });
    navigate(`/${ROUTES.EDIT_SHOP}`);
  };

  const onDuplicateClick = (productId: string) => {
    duplicateListing({ id: productId, shopId: shop.details?.id || '' });
    navigate(`/${ROUTES.EDIT_SHOP}`);
  };

  const onLikeClick = useRequireToken((productId: string, isLiked: boolean) => {
    onLikeToggle({ productId, isLiked, source: LikeProductSource.Shop });
  });

  const Catalog = (
    <CatalogSection
      list={catalog}
      shopId={id}
      isShopOwner={isShopOwner}
      hasNewItems={hasNewItems}
      onSeeAllCatalogClick={onSeeAllCatalogClick}
      onPreviewClick={onPreviewClick}
      onEmptyStateButtonClick={onAddItemToCatalogClick}
      onDeleteCatalogItemClick={onDeleteClick}
      onEditCatalogItemClick={onEditClick}
      onDuplicateCatalogItemClick={onDuplicateClick}
      onSaveItemClick={onLikeClick}
    />
  );

  return (
    <div className="shop-page-content">
      <Information isExpended={false} categories={categories} description={description} />
      {tip && isShopOwner && <Tip tip={tip} />}
      {vacationDetails && !isShopOwner && (
        <Message
          message={
            <>
              <span>{translations.SETTINGS.POPUPS.VACATION_MODE_SHOP_MESSAGE}</span>
              <span className="shop-message">{vacationDetails?.text}</span>
              <span>Last updated: {toDateFormatWithText(vacationDetails?.date || 0)}</span>
            </>
          }
          prefixIcon={<HelpCircle />}
          type="primary"
          className="vacation-message"
        />
      )}
      <div className="shop-page-information">
        <div className="side-bar">
          {isMobile && Catalog}
          {isShopOwner && (
            <AnalyticsSection
              totalViews={totalProfileViews}
              totalSaves={totalSavedProducts}
              onViewsClick={onViewsClick}
              onSavesClick={onSavesClick}
            />
          )}
          <ActivitiesSection
            isShopOwner={isShopOwner}
            list={activities}
            onActivityClick={onActivityClick}
            onActivitiesClick={onActivitiesClick}
            onEmptyStateButtonClick={onActivityEmptyStateClick}
          />
          {isShopOwner && (
            <div className="section">
              <GroupsSection groups={groups} onGroupsClick={onAllChatsClick} />
            </div>
          )}
        </div>
        <div className="inner-preview">
          {!isMobile && Catalog}
          <ReviewsSection onReviewsClick={onReviewsClick} list={reviews} />
          <SimilarShopsSection
            onFollowToggle={onFollowToggle}
            shops={similarShops}
            onShopClick={onShopClick}
            onSeeAllSimilarShops={onSeeAllSimilarShops}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IStore2) => {
  const { shop } = state;

  const { isPremium } = state.user;

  return {
    shop,
    isPremium,
  };
};

const mapDispatchToProps = {
  onLikeToggle: onProductLikeToggle,
  onFollowToggle: onFollowUserToggle,
  showPopup: PopupSlicer.actions.show,
  editShop: CreateShopSlicer.actions.editShop,
  editListing: CreateShopSlicer.actions.navigateToEditListingForm,
  duplicateListing: CreateShopSlicer.actions.navigateToDuplicateListingForm,
  updateProductPageSource: SpecificProductSlicer.actions.updateSource,
};

export default connect(mapStateToProps, mapDispatchToProps)(Home);
